/**
 * src/components/ledger/TransactionFilterBar.tsx
 * Instant search, multi-factor category, user, and type filter bar
 */

import React, { useState } from 'react';
import { Search, Filter, X, ArrowUpRight, ArrowDownLeft, Calendar } from 'lucide-react';
import type { UserItem, CategoryItem } from '../../services/api';

interface TransactionFilterBarProps {
  search: string;
  onSearchChange: (search: string) => void;
  selectedType: string;
  onTypeChange: (type: 'paid' | 'receive' | '') => void;
  selectedUser: string;
  onUserChange: (userId: string) => void;
  selectedCategory: string;
  onCategoryChange: (categoryId: string) => void;
  startDate?: string;
  onStartDateChange?: (date: string) => void;
  endDate?: string;
  onEndDateChange?: (date: string) => void;
  users: UserItem[];
  categories: CategoryItem[];
  isAdmin: boolean;
  onReset: () => void;
}

export const TransactionFilterBar: React.FC<TransactionFilterBarProps> = ({
  search,
  onSearchChange,
  selectedType,
  onTypeChange,
  selectedUser,
  onUserChange,
  selectedCategory,
  onCategoryChange,
  startDate = '',
  onStartDateChange,
  endDate = '',
  onEndDateChange,
  users,
  categories,
  isAdmin,
  onReset
}) => {
  const [showDatePicker, setShowDatePicker] = useState<boolean>(Boolean(startDate || endDate));

  const hasActiveFilters = Boolean(
    search || selectedType || selectedUser || selectedCategory || startDate || endDate
  );

  return (
    <div
      className="ios-inset-group"
      style={{
        padding: '14px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginBottom: '10px'
      }}
    >
      {/* Top Search & Reset Row */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        {/* iOS Native Search Input */}
        <div style={{ position: 'relative', flex: 1 }}>
          <Search
            size={16}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-tertiary)',
              pointerEvents: 'none'
            }}
          />
          <input
            type="text"
            className="glass-input"
            style={{
              paddingLeft: '38px',
              paddingRight: '36px',
              borderRadius: '10px',
              fontSize: '0.875rem'
            }}
            placeholder="Search descriptions, amounts, members..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          {search && (
            <button
              onClick={() => onSearchChange('')}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(118, 118, 128, 0.24)',
                border: 'none',
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0
              }}
            >
              <X size={12} />
            </button>
          )}
        </div>

        {hasActiveFilters && (
          <button
            onClick={onReset}
            className="glass-button glass-button-ghost"
            style={{ fontSize: '0.8125rem', padding: '8px 12px' }}
          >
            Clear
          </button>
        )}
      </div>

      {/* Filter Selectors Row - iOS Segmented Capsule */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Type Toggle Segmented Capsule */}
        <div className="ios-segmented-control" style={{ padding: '3px' }}>
          <button
            type="button"
            onClick={() => onTypeChange('')}
            className={`ios-segment-button ${selectedType === '' ? 'active' : ''}`}
            style={{ padding: '5px 12px', fontSize: '0.78rem' }}
          >
            All
          </button>

          <button
            type="button"
            onClick={() => onTypeChange('receive')}
            className={`ios-segment-button ${selectedType === 'receive' ? 'active' : ''}`}
            style={{
              padding: '5px 12px',
              fontSize: '0.78rem',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <ArrowUpRight size={13} color="var(--ios-green)" />
            <span>{isAdmin ? 'Received by Admin (+)' : 'Paid by Me (+)'}</span>
          </button>

          <button
            type="button"
            onClick={() => onTypeChange('paid')}
            className={`ios-segment-button ${selectedType === 'paid' ? 'active' : ''}`}
            style={{
              padding: '5px 12px',
              fontSize: '0.78rem',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <ArrowDownLeft size={13} color="var(--ios-red)" />
            <span>{isAdmin ? 'Paid by Admin (-)' : 'Received by Me (-)'}</span>
          </button>
        </div>

        {/* User Selector (for Admin) */}
        {isAdmin && (
          <select
            className="glass-select"
            value={selectedUser}
            onChange={(e) => onUserChange(e.target.value)}
            style={{ width: 'auto', minWidth: '140px', padding: '7px 12px', fontSize: '0.8125rem' }}
          >
            <option value="">All Members</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.username}</option>
            ))}
          </select>
        )}

        {/* Category Selector */}
        <select
          className="glass-select"
          value={selectedCategory}
          onChange={(e) => onCategoryChange(e.target.value)}
          style={{ width: 'auto', minWidth: '140px', padding: '7px 12px', fontSize: '0.8125rem' }}
        >
          <option value="">All Categories</option>
          {categories.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        {/* Date Filter Toggle Button */}
        {onStartDateChange && onEndDateChange && (
          <button
            type="button"
            onClick={() => setShowDatePicker(prev => !prev)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '7px 12px',
              borderRadius: 'var(--radius-button)',
              border: (startDate || endDate || showDatePicker) ? '1px solid var(--ios-blue)' : 'var(--border-hairline)',
              background: (startDate || endDate || showDatePicker) ? 'rgba(10, 132, 255, 0.16)' : 'rgba(118, 118, 128, 0.15)',
              color: (startDate || endDate || showDatePicker) ? 'var(--ios-blue)' : 'var(--text-secondary)',
              fontSize: '0.8125rem',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            <Calendar size={14} />
            <span>{(startDate || endDate) ? 'Filtered Date' : 'Date Range'}</span>
          </button>
        )}
      </div>

      {/* Collapsible Date-to-Date Range Controls */}
      {showDatePicker && onStartDateChange && onEndDateChange && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '10px',
          paddingTop: '10px',
          borderTop: 'var(--border-hairline)'
        }}>
          {/* Quick Presets */}
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              type="button"
              onClick={() => {
                const today = new Date().toISOString().slice(0, 10);
                onStartDateChange(today);
                onEndDateChange(today);
              }}
              style={{
                fontSize: '0.72rem',
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: 'var(--radius-pill)',
                background: 'rgba(118, 118, 128, 0.15)',
                border: 'var(--border-hairline)',
                color: 'var(--text-secondary)',
                cursor: 'pointer'
              }}
            >
              Today
            </button>

            <button
              type="button"
              onClick={() => {
                const now = new Date();
                const start = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
                const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
                const end = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
                onStartDateChange(start);
                onEndDateChange(end);
              }}
              style={{
                fontSize: '0.72rem',
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: 'var(--radius-pill)',
                background: 'rgba(118, 118, 128, 0.15)',
                border: 'var(--border-hairline)',
                color: 'var(--text-secondary)',
                cursor: 'pointer'
              }}
            >
              This Month
            </button>

            <button
              type="button"
              onClick={() => {
                const now = new Date();
                const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                const start = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}-01`;
                const lastDay = new Date(prev.getFullYear(), prev.getMonth() + 1, 0).getDate();
                const end = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
                onStartDateChange(start);
                onEndDateChange(end);
              }}
              style={{
                fontSize: '0.72rem',
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: 'var(--radius-pill)',
                background: 'rgba(118, 118, 128, 0.15)',
                border: 'var(--border-hairline)',
                color: 'var(--text-secondary)',
                cursor: 'pointer'
              }}
            >
              Last Month
            </button>
          </div>

          {/* Date Inputs */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', fontWeight: 600 }}>FROM:</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => onStartDateChange(e.target.value)}
                className="glass-input"
                style={{ padding: '4px 8px', fontSize: '0.78rem', height: '32px', width: 'auto' }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', fontWeight: 600 }}>TO:</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => onEndDateChange(e.target.value)}
                className="glass-input"
                style={{ padding: '4px 8px', fontSize: '0.78rem', height: '32px', width: 'auto' }}
              />
            </div>

            {(startDate || endDate) && (
              <button
                type="button"
                onClick={() => {
                  onStartDateChange('');
                  onEndDateChange('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--ios-red)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: '4px 8px'
                }}
              >
                Clear Date
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
